import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { userService } from '../../services/userService';
import { clientService } from '../../services/clientService';
import { UserCreate, User } from '../../types/user';
import { Client } from '../../types/client';
import config from '../../config/env';
import { safeStorage } from '../../utils/safeStorage';

// Role hierarchy for filtering (higher index = higher privilege)
const ROLE_HIERARCHY = ['Viewer', 'ClientAdmin', 'Admin', 'SuperAdmin'];

// Backward compat: treat Operator as ClientAdmin
const normalizeRoleName = (role: string): string => role === 'Operator' ? 'ClientAdmin' : role;

// Available roles in the system (from authorization.py)
const AVAILABLE_ROLES = [
  { id: 'Viewer', name: 'Viewer', description: 'Read-only access to all resources' },
  { id: 'ClientAdmin', name: 'ClientAdmin', description: 'Edit devices, templates; manage users within same clients' },
  { id: 'Admin', name: 'Admin', description: 'Full access including user management' },
  { id: 'SuperAdmin', name: 'SuperAdmin', description: 'Full administrative access' },
];

// The Keycloak client ID for role assignment. Sourced from runtime config
// so prod and dev realms can use different client names without code edits.
const KEYCLOAK_CLIENT_ID = config.oauth.clientId;

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUserCreated: (user: User) => void;
}

interface FormErrors {
  [key: string]: string;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose,
  onUserCreated
}) => {
  const { user: currentUser } = useAuth();
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showHelp, setShowHelp] = useState(false);

  // Form fields
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState(true);
  const [enabled, setEnabled] = useState(true);

  // Client and Role assignment
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<string>('Viewer');
  const [loadingClients, setLoadingClients] = useState(false);

  // Check if current user is SuperAdmin
  const isSuperAdmin = useMemo(() => {
    return currentUser?.roles?.includes('SuperAdmin') || false;
  }, [currentUser?.roles]);

  // Get the highest role level of the current user
  const currentUserRoleLevel = useMemo(() => {
    if (!currentUser?.roles) return -1;
    let maxLevel = -1;
    for (const role of currentUser.roles) {
      const level = ROLE_HIERARCHY.indexOf(normalizeRoleName(role));
      if (level > maxLevel) maxLevel = level;
    }
    return maxLevel;
  }, [currentUser?.roles]);

  // Get available roles that the current user can assign (only roles at or below their level)
  const availableRoles = useMemo(() => {
    if (isSuperAdmin) {
      return AVAILABLE_ROLES; // SuperAdmin can assign all roles
    }
    return AVAILABLE_ROLES.filter((role) => {
      const roleLevel = ROLE_HIERARCHY.indexOf(role.id);
      return roleLevel <= currentUserRoleLevel;
    });
  }, [isSuperAdmin, currentUserRoleLevel]);

  // Filter clients based on current user's client groups (unless SuperAdmin)
  const filteredClients = useMemo(() => {
    if (isSuperAdmin) {
      return clients; // SuperAdmin can see all clients
    }
    const currentUserClientIds = currentUser?.clientIds || [];
    if (currentUserClientIds.length === 0) {
      return clients; // If no client restriction, show all
    }
    return clients.filter(client => currentUserClientIds.includes(client.id));
  }, [clients, isSuperAdmin, currentUser?.clientIds]);

  // Fetch clients on mount
  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      setLoadingClients(true);
      const clientList = await clientService.fetchActiveClients();
      setClients(clientList);
    } catch (err) {
      console.error('[CREATE USER] Failed to fetch clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Username validation
    if (!username || username.trim() === '') {
      newErrors.username = 'Username is required';
    } else if (username.trim().length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (username.trim().length > 255) {
      newErrors.username = 'Username must not exceed 255 characters';
    } else if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      newErrors.username = 'Username can only contain letters, numbers, dots, underscores, and hyphens';
    }

    // Email validation
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (password) {
      if (password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Client validation
    if (!selectedClientId) {
      newErrors.client = 'Please select a client for this user';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const userData: UserCreate = {
        username: username.trim(),
        enabled
      };

      if (email) {
        userData.email = email.trim();
      }

      if (firstName) {
        userData.firstName = firstName.trim();
      }

      if (lastName) {
        userData.lastName = lastName.trim();
      }

      if (password) {
        userData.credentials = [{
          type: 'password',
          value: password,
          temporary: temporaryPassword
        }];

        if (temporaryPassword) {
          userData.requiredActions = ['UPDATE_PASSWORD'];
        }
      }

      const createdUser = await userService.createUser(userData);

      // Add user to the selected client's group. Failures here used to
      // be swallowed (console.error only), leaving the user provisioned
      // in Keycloak with no client group → empty allowedClientIds →
      // canManageDevices=false → "Add Device" hidden, with no signal to
      // the admin that anything went wrong. Surface the error instead;
      // the user already exists in Keycloak, so a retry from the Users
      // detail view (add to group, assign role) recovers.
      if (selectedClientId && createdUser.id) {
        const groupResponse = await fetch(`/api/v2/clients/${selectedClientId}/group`, {
          headers: {
            'Authorization': `Bearer ${safeStorage.getItem('access_token')}`
          }
        });
        if (!groupResponse.ok) {
          throw new Error(`User created but client group lookup failed (${groupResponse.status}). Open the user detail page to add them to "${selectedClientId}" manually.`);
        }
        const groupData = await groupResponse.json();
        if (!groupData?.id) {
          throw new Error('User created but the client group response was missing an id. Add to group manually from the user detail page.');
        }
        await userService.addUserToGroup(createdUser.id, groupData.id);
      }

      // Assign the selected role to the user. Same propagation reasoning
      // as the group block above — silent failures here produce an
      // Admin user with resource_access.provisioning-dev.roles=null,
      // and the role-based UI gates collapse without explanation.
      if (selectedRole && createdUser.id) {
        await userService.addClientRoleToUser(createdUser.id, KEYCLOAK_CLIENT_ID, selectedRole);
      }

      // Call the callback
      onUserCreated(createdUser);

      // Reset form
      resetForm();

      // Close modal
      onClose();
    } catch (err: any) {
      console.error('[CREATE USER] Error creating user:', err);

      // Extract error message
      let errorMessage = 'Failed to create user';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          if (detail.includes('already exists') || detail.includes('duplicate')) {
            errorMessage = `Username '${username}' already exists. Please choose a different username.`;
          } else {
            errorMessage = detail;
          }
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setConfirmPassword('');
    setTemporaryPassword(true);
    setEnabled(true);
    setSelectedClientId('');
    setSelectedRole('Viewer');
    setErrors({});
    setError(null);
    setShowHelp(false);
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content create-user-modal" data-testid="modal-create-user" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>{t('users.createNew')}</h2>
          </div>
          <div className="header-controls">
            <div className="help-toggle">
              <label className="toggle-switch small">
                <input
                  type="checkbox"
                  checked={showHelp}
                  onChange={(e) => setShowHelp(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <span className="toggle-label">{t('common.fieldHelp')}</span>
            </div>
            <button
              className="close-button"
              data-testid="btn-modal-close"
              onClick={handleClose}
              disabled={isLoading}
              aria-label={t('common.close')}
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner">
                <span className="error-icon">!</span>
                <span className="error-message">{error}</span>
                <button
                  type="button"
                  className="error-dismiss"
                  onClick={() => setError(null)}
                  aria-label={t('common.dismissError')}
                >
                  ×
                </button>
              </div>
            )}

            <div className="form-section">
              <h4>{t('users.accessConfiguration')}</h4>
              <p className="section-description">
                Configure the client and role for this user. Users can only access data belonging to their assigned client.
              </p>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="client" className="required">
                    Client <span className="required-asterisk">*</span>
                  </label>
                  <select
                    id="client"
                    data-testid="field-user-client"
                    value={selectedClientId}
                    onChange={(e) => {
                      setSelectedClientId(e.target.value);
                      if (errors.client) {
                        setErrors({ ...errors, client: '' });
                      }
                    }}
                    className={`form-select ${errors.client ? 'error' : ''}`}
                    disabled={isLoading || loadingClients}
                  >
                    <option value="">-- Select a client --</option>
                    {filteredClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                  {errors.client && (
                    <span className="error-text">{errors.client}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="role" className="required">
                    Role <span className="required-asterisk">*</span>
                  </label>
                  <select
                    id="role"
                    data-testid="field-user-role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="form-select"
                    disabled={isLoading}
                  >
                    {availableRoles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {showHelp && (
                <div className="field-help-row">
                  <div className="field-help">
                    <div className="help-label">Client</div>
                    <div className="help-description">
                      The user will be added to this client's group, giving them access to view and manage devices for this client only.
                    </div>
                  </div>
                  <div className="field-help">
                    <div className="help-label">Role</div>
                    <div className="help-description">
                      {availableRoles.find(r => r.id === selectedRole)?.description || 'Select a role'}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h4>{t('users.accountInformation')}</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="username" className="required">
                    Username <span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="text"
                    id="username"
                    data-testid="field-user-username"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      if (errors.username) {
                        setErrors({ ...errors, username: '' });
                      }
                    }}
                    className={errors.username ? 'error' : ''}
                    placeholder="e.g., john.doe"
                    disabled={isLoading}
                    autoFocus
                    maxLength={255}
                  />
                  {errors.username && (
                    <span className="error-text">{errors.username}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    data-testid="field-user-email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) {
                        setErrors({ ...errors, email: '' });
                      }
                    }}
                    className={errors.email ? 'error' : ''}
                    placeholder="email@example.com"
                    disabled={isLoading}
                  />
                  {errors.email && (
                    <span className="error-text">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">{t('users.firstName')}</label>
                  <input
                    type="text"
                    id="firstName"
                    data-testid="field-user-first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('users.firstNamePlaceholder')}
                    disabled={isLoading}
                    maxLength={255}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">{t('users.lastName')}</label>
                  <input
                    type="text"
                    id="lastName"
                    data-testid="field-user-last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t('users.lastNamePlaceholder')}
                    disabled={isLoading}
                    maxLength={255}
                  />
                </div>
              </div>

              {showHelp && (
                <div className="field-help">
                  <div className="help-description">
                    Username must be unique. Can contain letters, numbers, dots, underscores, and hyphens.
                  </div>
                </div>
              )}
            </div>

            <div className="form-section">
              <h4>Password</h4>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    data-testid="field-user-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.password) {
                        setErrors({ ...errors, password: '' });
                      }
                    }}
                    className={errors.password ? 'error' : ''}
                    placeholder={t('users.passwordMinPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.password && (
                    <span className="error-text">{errors.password}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">{t('users.confirmPassword')}</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    data-testid="field-user-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (errors.confirmPassword) {
                        setErrors({ ...errors, confirmPassword: '' });
                      }
                    }}
                    className={errors.confirmPassword ? 'error' : ''}
                    placeholder={t('users.confirmPasswordPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.confirmPassword && (
                    <span className="error-text">{errors.confirmPassword}</span>
                  )}
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    data-testid="field-user-temporary"
                    checked={temporaryPassword}
                    onChange={(e) => setTemporaryPassword(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="checkbox-text">Temporary password (user must change on first login)</span>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h4>{t('users.accountStatus')}</h4>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    data-testid="field-user-enabled"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span className="checkbox-text">{t('users.accountEnabled')}</span>
                </label>
                {showHelp && (
                  <div className="field-help">
                    <div className="help-description">
                      Disabled accounts cannot log in to the system.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-testid="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              data-testid="btn-submit"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
